import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import Header from '../../components/Header';
import DropDown from '../../components/DropDown/DropDown';
import ModalStandalone, { IModalHandle } from '../../lib/ModalStandalone';
import LeaveRejectModal from '../../components/LeaveRejectModal/LeaveRejectModal';
import LeaveStatusBadge from '../../components/LeaveStatusBadge/LeaveStatusBadge';
import LeaveTrackingModal from '../../components/LeaveTrackingModal/LeaveTrackingModal';
import { getNotifsAction } from '../../actions/notif';
import { getManagementLeavesAction, patchLeaveAction } from '../../actions/leave';
import { getAllStructuresAction } from '../../actions/structures';
import { LEAVE_STATUS_LABELS, leaveTypeLabel } from '../../constants/leave';
import { ILeave, LeaveStatus } from '../../types/leave';
import { IPole, IStructure } from '../../types/structure';

import styles from './AdminLeaves.module.scss';

const LEAVE_STATUS_OPTIONS: LeaveStatus[] = [
  'pending',
  'to_plan',
  'approved',
  'rejected',
  'cancelled',
];

const formatDate = (iso?: string) => {
  if (!iso) return '-';
  try {
    return format(new Date(iso), 'dd/MM/yyyy');
  } catch {
    return '-';
  }
};

const leaveStructureId = (leave: ILeave): string | null => {
  const s = leave.structure;
  if (!s) return null;
  if (typeof s === 'object' && '_id' in s) return String((s as { _id: string })._id);
  return String(s);
};

const leaveUserPoles = (leave: ILeave): string[] => {
  const u = leave.user;
  if (u && typeof u === 'object' && Array.isArray(u.poles)) {
    return u.poles.map(String);
  }
  return [];
};

const AdminLeaves = () => {
  const dispatch = useDispatch();
  const rejectModalRef = useRef<IModalHandle>(null);
  const trackingModalRef = useRef<IModalHandle>(null);
  const [rejectLeaveId, setRejectLeaveId] = useState<string | null>(null);
  const [trackingLeave, setTrackingLeave] = useState<ILeave | null>(null);
  const [selectedStructure, setSelectedStructure] = useState<string | null>(null);
  const [selectedPoles, setSelectedPoles] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<LeaveStatus[]>([]);
  const [structureReady, setStructureReady] = useState(false);

  const { user } = useSelector((store: any) => store.authReducer);
  const { structuresList } = useSelector((store: any) => store.structureReducer);
  const { managementLeaves, isLoading } = useSelector((store: any) => store.leaveReducer);

  const canSelectStructure = user?.role === 'Admin';

  useEffect(() => {
    getAllStructuresAction(dispatch as any);
    getNotifsAction(dispatch as any).catch(() => {});
  }, [dispatch]);

  useEffect(() => {
    if (structureReady || !user) return;
    if (user.role === 'Admin') {
      // Admin: all structures by default
      setSelectedStructure(null);
      setStructureReady(true);
      return;
    }
    if (user?.structure?._id) {
      setSelectedStructure(user.structure._id);
      setStructureReady(true);
    }
  }, [user, structureReady]);

  useEffect(() => {
    if (!structureReady) return;
    if (user?.role !== 'Admin' && !selectedStructure) return;
    const structureParam = user?.role === 'Admin' ? selectedStructure : null;
    getManagementLeavesAction(dispatch as any, structureParam);
  }, [dispatch, selectedStructure, user?.role, structureReady]);

  const polesList = useMemo(() => {
    if (!structuresList?.length || !selectedStructure) return [];
    const structure = structuresList.find(
      (s: IStructure) => s._id === selectedStructure,
    );
    return structure?.poles || [];
  }, [structuresList, selectedStructure]);

  const poles = useMemo(() => polesList.filter((po: IPole) => {
    if (user?.role === 'Manager') {
      return Boolean(user?.poles?.find((p: string) => p === po._id));
    }
    return true;
  }), [polesList, user]);

  const showCategoriesFilter = Boolean(selectedStructure) && poles.length > 0;

  const filteredLeaves = useMemo(() => {
    if (!managementLeaves?.length) return [];
    return managementLeaves.filter((l: ILeave) => {
      if (selectedStructure) {
        const sid = leaveStructureId(l);
        if (sid !== selectedStructure) return false;
      }
      if (showCategoriesFilter && selectedPoles.length > 0) {
        const userPoles = leaveUserPoles(l);
        if (!selectedPoles.some((p) => userPoles.includes(p))) return false;
      }
      if (selectedStatuses.length > 0) {
        if (!selectedStatuses.includes(l.status)) return false;
      }
      return true;
    });
  }, [
    managementLeaves,
    selectedStructure,
    selectedPoles,
    selectedStatuses,
    showCategoriesFilter,
  ]);

  const openReject = (id: string) => {
    setRejectLeaveId(id);
    rejectModalRef.current?.open();
  };

  const closeReject = () => {
    rejectModalRef.current?.close();
    setRejectLeaveId(null);
  };

  const openTracking = (l: ILeave) => {
    setTrackingLeave(l);
    trackingModalRef.current?.open();
  };

  const closeTracking = () => {
    trackingModalRef.current?.close();
    setTrackingLeave(null);
  };

  const handleRejectConfirm = async (reason: string) => {
    if (!rejectLeaveId) return;
    const res: any = await patchLeaveAction(dispatch as any, rejectLeaveId, {
      status: 'rejected',
      rejectionReason: reason,
    });
    if (res?.data?.leave) {
      closeReject();
      getNotifsAction(dispatch as any).catch(() => {});
    }
  };

  const validateToPlan = async (id: string) => {
    const res: any = await patchLeaveAction(dispatch as any, id, { status: 'to_plan' });
    if (res?.data?.leave) {
      getNotifsAction(dispatch as any).catch(() => {});
    }
  };

  const markPlanned = async (id: string) => {
    const res: any = await patchLeaveAction(dispatch as any, id, { status: 'approved' });
    if (res?.data?.leave) {
      getNotifsAction(dispatch as any).catch(() => {});
    }
  };

  const handleStructureChange = (value: string | null) => {
    setSelectedStructure(value);
    setSelectedPoles([]);
    if (value) {
      localStorage.setItem('structure', value);
    }
  };

  const structureName = (leave: ILeave) => {
    const s = leave.structure;
    if (s && typeof s === 'object' && 'name' in s) return (s as { name?: string }).name || '-';
    return '-';
  };

  const userName = (leave: ILeave) => {
    const u = leave.user;
    if (u && typeof u === 'object' && u.profile) {
      return `${u.profile.firstName} ${u.profile.lastName}`;
    }
    return '-';
  };

  const leavesCount = filteredLeaves.length;
  const patchBusy = isLoading?.includes('PATCH_LEAVE');

  return (
    <div className={styles.container}>
      <Header name="Congés" isProfile />
      <div className={styles.content}>
        <div className={styles.title}>
          <div className={styles.heading}>
            <h1>Demandes de congés</h1>
            <p>{leavesCount}</p>
          </div>
        </div>

        <div className={styles.filters}>
          {canSelectStructure && structuresList?.length > 0 && (
            <div className={styles.filter}>
              <DropDown
                onChange={handleStructureChange}
                selected={selectedStructure}
                options={[
                  { label: 'Toutes les structures', value: null },
                  ...structuresList.map((s: IStructure) => ({
                    label: s.name,
                    value: s._id,
                  })),
                ]}
              />
            </div>
          )}
          {showCategoriesFilter && (
            <div className={styles.filter}>
              <DropDown
                onChange={(value: string[]) => setSelectedPoles(value)}
                selected={selectedPoles}
                multiLabel={
                  selectedPoles.length > 0
                    ? 'catégories séléctionnées'
                    : 'Catégories'
                }
                isMulti
                options={[
                  { label: 'Toutes les catégories', value: null },
                  ...poles.map((s: IPole) => ({
                    label: s.name,
                    value: s._id,
                    color: s.color,
                  })),
                ]}
              />
            </div>
          )}
          <div className={styles.filter}>
            <DropDown
              onChange={(value: LeaveStatus[]) => setSelectedStatuses(value)}
              selected={selectedStatuses}
              multiLabel={
                selectedStatuses.length > 0
                  ? 'statuts séléctionnés'
                  : 'Statut'
              }
              isMulti
              options={[
                { label: 'Tous les statuts', value: null },
                ...LEAVE_STATUS_OPTIONS.map((status) => ({
                  label: LEAVE_STATUS_LABELS[status],
                  value: status,
                })),
              ]}
            />
          </div>
        </div>

        {leavesCount === 0
          ? <p className={styles.empty}>Aucune demande.</p>
          : (
            <div className={styles.list}>
              <div className={styles.grid}>
                {filteredLeaves.map((l: ILeave) => (
                  <div key={l._id} className={styles.card}>
                    <div className={styles.badge}>
                      <LeaveStatusBadge status={l.status} />
                    </div>
                    <div className={styles.cardTop}>
                      <div className={styles.infos}>
                        <h3>
                          {formatDate(l.startDate)}
                          <br />
                          → Retour le {formatDate(l.endDate)}
                        </h3>
                        <h4>
                          Type : {leaveTypeLabel(l.leaveType, l.leaveTypeComment)}
                        </h4>
                        <h4>Structure : {structureName(l)}</h4>
                        <h4>Demandeur : {userName(l)}</h4>
                        {l.status === 'rejected' && l.rejectionReason && (
                          <h4>Motif : {l.rejectionReason}</h4>
                        )}
                      </div>
                    </div>
                    <div className={styles.actions}>
                      <button type="button" onClick={() => openTracking(l)}>
                        Voir le détail du suivi
                      </button>
                      {l.status === 'pending' && (
                        <>
                          <button
                            type="button"
                            disabled={patchBusy}
                            onClick={() => validateToPlan(l._id)}
                          >
                            Valider
                          </button>
                          <button
                            type="button"
                            disabled={patchBusy}
                            onClick={() => openReject(l._id)}
                          >
                            Refuser
                          </button>
                        </>
                      )}
                      {l.status === 'to_plan' && (
                        <button
                          type="button"
                          disabled={patchBusy}
                          onClick={() => markPlanned(l._id)}
                        >
                          Marquer comme planifié
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
      </div>

      <ModalStandalone
        ref={rejectModalRef}
        minWidth={520}
        closeCallBack={() => setRejectLeaveId(null)}
      >
        <LeaveRejectModal onClose={closeReject} onConfirm={handleRejectConfirm} />
      </ModalStandalone>

      <ModalStandalone
        ref={trackingModalRef}
        minWidth={640}
        closeCallBack={() => setTrackingLeave(null)}
      >
        {trackingLeave && (
          <LeaveTrackingModal leave={trackingLeave} onClose={closeTracking} />
        )}
      </ModalStandalone>
    </div>
  );
};

export default AdminLeaves;
